import type { Assignee, Card, Status } from './types'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status)
  }
  return res.status === 204 ? (undefined as T) : res.json()
}

export type CardPatch = Partial<{
  title: string
  notes: string | null
  assignee: Assignee
  dueDate: string | null
  waitingOn: string | null
  status: Status
  /** Destination index within the column, excluding the card being moved. */
  index: number
  done: boolean
}>

export const api = {
  session: () => request<{ ok: true }>('/api/session'),

  authenticate: (pw: string) =>
    request<{ ok: true }>('/api/auth', { method: 'POST', body: JSON.stringify({ pw }) }),

  listCards: () => request<{ active: Card[]; done: Card[] }>('/api/cards'),

  createCard: (input: { title: string; assignee: Assignee; status: Status }) =>
    request<Card>('/api/cards', { method: 'POST', body: JSON.stringify(input) }),

  updateCard: (id: string, patch: CardPatch) =>
    request<Card>(`/api/cards/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  deleteCard: (id: string) => request<{ ok: true }>(`/api/cards/${id}`, { method: 'DELETE' }),
}
